import type { MyAnnouncementFieldsFragment, MySurveyFieldsFragment } from '@paintscout/api'

export type Survey = MySurveyFieldsFragment

/** sessionStorage key: set once an on-login survey has opened by itself in this tab (#25349). */
export const SESSION_SURVEY_SHOWN_KEY = 'ps-survey-shown'

/**
 * The on-login survey that opens by itself now, or null: the newest one the user has neither
 * answered nor dismissed. At most one opens per session (`alreadyShownThisSession`).
 */
export function pickOnLoginSurvey(surveys: readonly Survey[], alreadyShownThisSession: boolean): Survey | null {
  if (alreadyShownThisSession) return null
  return (
    [...surveys]
      .sort((a, b) => (b.publishedAt || '').localeCompare(a.publishedAt || ''))
      .find((s) => s.trigger === 'on-login' && !s.answeredAt && !s.dismissedAt) ?? null
  )
}

/**
 * The "once" rule for a page-view survey (#25610): neither answered nor dismissed (stored state),
 * and not already opened in this tab. Page-view surveys are not limited to one per session.
 */
export function isPageViewSurveyCandidate(survey: Survey, shownInTab: ReadonlySet<string>): boolean {
  return !survey.answeredAt && !survey.dismissedAt && !shownInTab.has(survey.id)
}

/** The live manual survey behind a trigger key, or null. */
export function findManualSurvey(surveys: readonly Survey[], key: string): Survey | null {
  return surveys.find((s) => s.trigger === 'manual' && s.key === key) ?? null
}

/**
 * Whether the announcement that AnnouncementCenter opened by itself this load is closed, so a
 * survey may open after it. AnnouncementCenter marks it `dismissed` on close and `ctaClicked` on
 * its button, and both land in the shared `myEngagement` cache. `pendingId` null: none opened.
 * An announcement that left the list (expired, archived) no longer blocks.
 */
export function isAnnouncementSettled(
  announcements: readonly Pick<MyAnnouncementFieldsFragment, 'id' | 'dismissedAt' | 'ctaClickedAt'>[],
  pendingId: string | null
): boolean {
  if (!pendingId) return true
  const announcement = announcements.find((a) => a.id === pendingId)
  return !announcement || !!announcement.dismissedAt || !!announcement.ctaClickedAt
}

/** Whether an on-login survey already opened in this tab. False when storage throws. */
export function readSessionSurveyShown(storage: Pick<Storage, 'getItem'> | undefined = safeSessionStorage()): boolean {
  try {
    return !!storage?.getItem(SESSION_SURVEY_SHOWN_KEY)
  } catch {
    return false
  }
}

/** Records that an on-login survey opened in this tab. Never throws. */
export function writeSessionSurveyShown(
  id: string,
  storage: Pick<Storage, 'setItem'> | undefined = safeSessionStorage()
): void {
  try {
    storage?.setItem(SESSION_SURVEY_SHOWN_KEY, id)
  } catch {
    // Private mode or blocked storage: the in-memory guard in SurveyCenter still holds.
  }
}

function safeSessionStorage(): Storage | undefined {
  try {
    return typeof window === 'undefined' ? undefined : window.sessionStorage
  } catch {
    return undefined
  }
}
